import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _excluded = ["onClick", "upActive", "downActive", "label", "labelAlign", "upLabel", "downLabel", "upCount", "downCount", "className", "readOnly", "id"];
/*
 * © 2022 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import { ThumbContainer, ThumbsRoot, ROOT_PREFIX } from './thumbsRatingStyles';
import { fillIcon, emptyIcon, hoverIcon } from './thumbsRatingIcons';
import { onEnterKeyPress } from '../utils/wcagUtils';
import { useIntl } from 'react-intl';
import { useId } from '../utils/useId';
export var Thumb = function Thumb(_ref) {
  var id = _ref.id,
    type = _ref.type,
    active = _ref.active,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'medium' : _ref$size,
    _ref$label = _ref.label,
    label = _ref$label === void 0 ? '' : _ref$label,
    count = _ref.count,
    _ref$readOnly = _ref.readOnly,
    readOnly = _ref$readOnly === void 0 ? false : _ref$readOnly,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    onClick = _ref.onClick,
    tabIndex = _ref.tabIndex;
  var intl = useIntl();
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isHover = _useState2[0],
    setHover = _useState2[1];
  var editable = !disabled && !readOnly;
  var classes = classnames("".concat(ROOT_PREFIX, "-icon"), "".concat(ROOT_PREFIX, "-icon-").concat(size), "icon-".concat(type), "".concat(readOnly ? 'readOnly' : ''), "".concat(disabled ? 'disabled' : ''));
  var _onClick = function _onClick() {
    if (editable) {
      onClick === null || onClick === void 0 || onClick();
      setHover(false);
    }
  };
  return /*#__PURE__*/React.createElement(ThumbContainer, {
    id: id,
    role: editable ? 'button' : 'img',
    onClick: _onClick,
    onKeyDown: function onKeyDown(e) {
      return onEnterKeyPress(e, _onClick);
    },
    onMouseEnter: function onMouseEnter() {
      return editable && !active && setHover(true);
    },
    onMouseLeave: function onMouseLeave() {
      return editable && setHover(false);
    },
    tabIndex: (tabIndex !== null && tabIndex !== void 0 ? tabIndex : readOnly || disabled) ? -1 : 0,
    "aria-label": "".concat(intl.formatMessage({
      id: "embers.thumbRating.".concat(type, ".label"),
      defaultMessage: "Thumbs ".concat(type)
    })).concat(count || count === 0 ? " (".concat(count, ")") : '', " ").concat(active ? intl.formatMessage({
      id: 'embers.starRating.selelcted',
      defaultMessage: 'selected'
    }) : ''),
    "aria-pressed": !disabled && !readOnly ? active : undefined,
    "aria-live": "assertive",
    className: classnames(_defineProperty(_defineProperty({}, "".concat(ROOT_PREFIX, "-thumb-readOnly"), readOnly), "".concat(ROOT_PREFIX, "-thumb-disabled"), disabled), "".concat(ROOT_PREFIX, "-thumb"))
  }, /*#__PURE__*/React.createElement("span", {
    className: classes
  }, isHover ? hoverIcon : active ? fillIcon : emptyIcon), label && /*#__PURE__*/React.createElement("span", {
    "aria-hidden": true,
    className: "".concat(ROOT_PREFIX, "-thumb-label")
  }, count || count === 0 ? "".concat(label, " (").concat(count, ")") : label));
};
export var ThumbsRating = function ThumbsRating(_ref2) {
  var onClick = _ref2.onClick,
    upActive = _ref2.upActive,
    downActive = _ref2.downActive,
    _ref2$label = _ref2.label,
    label = _ref2$label === void 0 ? '' : _ref2$label,
    _ref2$labelAlign = _ref2.labelAlign,
    labelAlign = _ref2$labelAlign === void 0 ? 'right' : _ref2$labelAlign,
    upLabel = _ref2.upLabel,
    downLabel = _ref2.downLabel,
    upCount = _ref2.upCount,
    downCount = _ref2.downCount,
    className = _ref2.className,
    _ref2$readOnly = _ref2.readOnly,
    readOnly = _ref2$readOnly === void 0 ? false : _ref2$readOnly,
    _id = _ref2.id,
    otherProps = _objectWithoutProperties(_ref2, _excluded);
  var id = useId(_id);
  var _useState3 = useState(),
    _useState4 = _slicedToArray(_useState3, 2),
    activeAction = _useState4[0],
    setActiveAction = _useState4[1];
  var thumbClick = function thumbClick(action) {
    // support deselect
    var result = activeAction !== action ? action : null;
    onClick === null || onClick === void 0 || onClick(result);
    setActiveAction(result);
  };
  useEffect(function () {
    if (upActive || downActive) {
      var _activeAction = upActive ? 'up' : 'down';
      setActiveAction(_activeAction);
    } else {
      setActiveAction(null);
    }
  }, [upActive, downActive]);
  return /*#__PURE__*/React.createElement(ThumbsRoot, {
    role: "group",
    "aria-labelledby": "thumbs-rating-label-".concat(id),
    className: classnames("".concat(ROOT_PREFIX, "-root ").concat(ROOT_PREFIX, "-root-").concat(otherProps.size), className)
  }, /*#__PURE__*/React.createElement(Thumb, _extends({
    id: "thumbs-rating-up-".concat(id),
    label: upLabel,
    count: upCount,
    active: readOnly && upActive ? upActive : activeAction === 'up',
    type: "up",
    onClick: function onClick() {
      return thumbClick('up');
    },
    readOnly: readOnly
  }, otherProps)), /*#__PURE__*/React.createElement(Thumb, _extends({
    id: "thumbs-rating-down-".concat(id),
    label: downLabel,
    count: downCount,
    active: readOnly && downActive ? downActive : activeAction === 'down',
    type: "down",
    onClick: function onClick() {
      return thumbClick('down');
    },
    readOnly: readOnly
  }, otherProps)), label && /*#__PURE__*/React.createElement("span", {
    id: "thumbs-rating-label-".concat(id),
    "aria-hidden": true,
    className: "".concat(ROOT_PREFIX, "-label label-").concat(labelAlign)
  }, label));
};